"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, Flame, Radio, CircleCheck as CheckCircle2, UserCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const applicationCards = [{
  title: "Safety and Security",
  description: "Rapid analytical identification of hazardous substances, chemical agents, and suspicious materials in field environments.",
  url: "/SafetyAndSecurityPage",
  icon: ShieldCheck,
  useCases: "Narcotics, CBRNe, Fire & HazMat",
  tag1: "Narcotics",
  tag2: "CBRNe",
  tag3: "Fire & HazMat"
}, {
  title: "Law Enforcement & Public Safety",
  description: "Direct-evidence field testing, non-destructive illicit substance profiling, and forensic analysis tools for operational teams.",
  url: "/LawEnforcementPublicSafetyPage",
  icon: Radio,
  useCases: "Law Enforcement, Narcotics, Explosives",
  tag1: "Law Enforcement",
  tag2: "Narcotics",
  tag3: "Explosives"
}, {
  title: "Border Security and Defence",
  description: "High-throughput screening, perimeter protection, and tactical identification for customs, checkpoints, and military applications.",
  url: "/BorderSecurityAndDefencePage",
  icon: Flame,
  useCases: "Border, Explosives, CBRNe",
  tag1: "Border",
  tag2: "Explosives",
  tag3: "CBRNe"
}];
const consultationHighlights = [{
  text: "Tailored instrument configuration for your target compounds"
}, {
  text: "On-site feasibility and validation protocols"
}, {
  text: "Lifecycle service, calibration, and operational training"
}];
export default function ApplicationSolutions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        duration: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="applicationsolutionscta" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-medium">
            <Sparkles className="size-3.5 mr-1 text-primary" />
            Field & Analytical Solutions
          </Badge>
          <h2 className="text-2xl md:text-4xl font-semibold tracking-tight text-foreground">
            Specialized Analytical Technology for Field & Regulated Operations
          </h2>
          <p className="mt-3 text-base md:text-lg text-muted-foreground">
            Targeted spectroscopy, testing, and detection platforms engineered for high-consequence screening, emergency response, and border operations across Ireland and the UK.
          </p>
        </div>

        {/* 8/4 Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column (8 cols): Application Cards */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {applicationCards.map((card, i) => {
            const IconComponent = card.icon;
            const isFullWidth = i === 2;
            return <div key={card.title} data-index={i} className={isFullWidth ? "md:col-span-2" : "md:col-span-1"}>
                  <motion.div variants={itemVariants} className="h-full">
                    <Card className="h-full flex flex-col justify-between border-border hover:border-primary/50 shadow-xs transition-[border-color,box-shadow] duration-200">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between gap-4 mb-2">
                          <div className="p-2.5 rounded-lg bg-secondary text-primary inline-flex">
                            <IconComponent className="size-5" />
                          </div>
                          <span className="text-xs font-mono text-muted-foreground">
                            0{i + 1}
                          </span>
                        </div>
                        <CardTitle className="text-lg md:text-xl font-semibold text-foreground">
                          {card.title}
                        </CardTitle>
                        <CardDescription className="text-sm leading-relaxed mt-1.5">
                          {card.description}
                        </CardDescription>
                      </CardHeader>

                      <CardContent className="pb-4">
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          <Badge variant="outline" className="text-xs bg-muted/40">
                            {card.tag1}
                          </Badge>
                          <Badge variant="outline" className="text-xs bg-muted/40">
                            {card.tag2}
                          </Badge>
                          <Badge variant="outline" className="text-xs bg-muted/40">
                            {card.tag3}
                          </Badge>
                        </div>
                      </CardContent>

                      <CardFooter className="pt-3 border-t border-border/60">
                        <Button asChild variant="ghost" className="w-full justify-between px-0 font-medium text-foreground hover:text-primary">
                          <Link to={card.url}>
                            <span>View Applications</span>
                            <ArrowRight className="size-4" />
                          </Link>
                        </Button>
                      </CardFooter>
                    </Card>
                  </motion.div>
                </div>;
          })}
          </motion.div>

          {/* Right Column (4 cols): Consultation Panel */}
          <motion.div initial={{
          opacity: shouldReduceMotion ? 1 : 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-4">
            <div className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-xs flex flex-col justify-between">
              <div>
                <div className="inline-flex p-3 rounded-lg bg-secondary text-primary mb-4">
                  <UserCheck className="size-6" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">
                  Consult an Application Specialist
                </h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Evaluate spectroscopic instrumentation and sampling configurations for your operational requirements with Antech Solutions technical engineers.
                </p>

                <Separator className="my-6" />

                <div className="space-y-3">
                  {consultationHighlights.map((item, index) => <div key={item.text} data-index={index} className="flex items-start gap-3">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-xs md:text-sm text-foreground">
                        {item.text}
                      </span>
                    </div>)}
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-border">
                <Button asChild size="lg" className="w-full font-medium">
                  <Link to="/DiscussYourApplicationPage">
                    Connect with an Application Specialist
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <div className="mt-3 text-center">
                  <Link to="/ApplicationConsultationPage" className="text-xs text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline">
                    Learn more about our consultation workflow
                  </Link>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}